// components/IntakeForm.jsx
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { INDUSTRY_OPTIONS, TIMELINE_OPTIONS, BUDGET_OPTIONS } from '../../../data/constants';

const IntakeForm = ({ projectType, formData, onUpdate, onNext }) => {
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    onUpdate({ [field]: value });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.contactName.trim()) newErrors.contactName = 'Contact name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.projectScope.trim()) newErrors.projectScope = 'Project scope is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <div>
      <div className="text-center mb-4 sm:mb-8 md:mb-12">
        <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-10 py-1 rounded-full text-xs font-medium mb-2 sm:mb-4">
          <Icon name={projectType?.icon || 'FileText'} size={16} strokeWidth={2} />
          <span>{projectType?.title || 'Project Details'}</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-2 sm:mb-4">
          Tell Us About Your Project
        </h2>
        <p className="text-base text-text-secondary max-w-3xl mx-auto">
          Help us understand your vision and requirements.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-6">
        <div className="bg-surface rounded-xl p-2 sm:p-6">
          <h3 className="text-xl font-bold text-primary mb-2 sm:mb-6 flex items-center space-x-2">
            <Icon name="Building" size={20} strokeWidth={2} />
            <span>Company Information</span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-6">
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Company Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                className={`w-full px-4 py-1 sm:py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-accent cinematic-transition ${
                  errors.companyName ? 'border-error' : 'border-border'
                }`}
                placeholder="Your company name"
              />
              {errors.companyName && (
                <p className="text-error text-sm mt-1">{errors.companyName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Industry
              </label>
              <select
                value={formData.industry || ''}
                onChange={(e) => handleInputChange('industry', e.target.value)}
                className="w-full px-4 py-1 sm:py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-accent cinematic-transition"
              >
                <option value="">Select industry</option>
                {INDUSTRY_OPTIONS.map((industry, index) => (
                  <option key={index} value={industry.toLowerCase().replace(/[ &]/g, '-')}>
                    {industry}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-surface rounded-xl p-2 sm:p-6">
          <h3 className="text-xl font-bold text-primary mb-2 sm:mb-6 flex items-center space-x-2">
            <Icon name="User" size={20} strokeWidth={2} />
            <span>Contact Information</span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-6">
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Your Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={formData.contactName}
                onChange={(e) => handleInputChange('contactName', e.target.value)}
                className={`w-full px-4 py-1 sm:py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-accent cinematic-transition ${
                  errors.contactName ? 'border-error' : 'border-border'
                }`}
                placeholder="Your full name"
              />
              {errors.contactName && (
                <p className="text-error text-sm mt-1">{errors.contactName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Job Title
              </label>
              <input
                type="text"
                value={formData.jobTitle || ''}
                onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                className="w-full px-4 py-1 sm:py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-accent cinematic-transition"
                placeholder="Your job title"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Email Address <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full px-4 py-1 sm:py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-accent cinematic-transition ${
                  errors.email ? 'border-error' : 'border-border'
                }`}
                placeholder="your.email@company.com"
              />
              {errors.email && (
                <p className="text-error text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Phone Number <span className="text-red-600">*</span>
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`w-full px-4 py-1 sm:py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-accent cinematic-transition ${
                  errors.phone ? 'border-error' : 'border-border'
                }`}
                placeholder="+254 (712) 345-678"
              />
              {errors.phone && (
                <p className="text-error text-sm mt-1">{errors.phone}</p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-surface rounded-xl p-2 sm:p-6">
          <h3 className="text-xl font-bold text-primary mb-2 sm:mb-6 flex items-center space-x-2">
            <Icon name="Target" size={20} strokeWidth={2} />
            <span>Project Details</span>
          </h3>
          
          <div className="space-y-2 sm:space-y-6">
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Project Scope & Description <span className="text-red-600">*</span>
              </label>
              <textarea
                value={formData.projectScope}
                onChange={(e) => handleInputChange('projectScope', e.target.value)}
                rows={4}
                className={`w-full px-4 py-1 sm:py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-accent cinematic-transition resize-none ${
                  errors.projectScope ? 'border-error' : 'border-border'
                }`}
                placeholder="Describe your project in detail..."
              />
              {errors.projectScope && (
                <p className="text-error text-sm mt-1">{errors.projectScope}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Project Objectives
              </label>
              <textarea
                value={formData.objectives}
                onChange={(e) => handleInputChange('objectives', e.target.value)}
                rows={3}
                className="w-full px-4 py-1 sm:py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-accent cinematic-transition resize-none"
                placeholder="What business goals do you want to achieve?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Target Audience
              </label>
              <textarea
                value={formData.targetAudience || ''}
                onChange={(e) => handleInputChange('targetAudience', e.target.value)}
                rows={3}
                className="w-full px-4 py-1 sm:py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-accent cinematic-transition resize-none"
                placeholder="Who is your target audience? (Demographics, interests, behaviors, etc.)"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-6">
              <div>
                <label className="block text-sm font-semibold text-primary mb-2">
                  Preferred Timeline
                </label>
                <select
                  value={formData.timeline}
                  onChange={(e) => handleInputChange('timeline', e.target.value)}
                  className="w-full px-4 py-1 sm:py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-accent cinematic-transition"
                >
                  <option value="">Select timeline</option>
                  {TIMELINE_OPTIONS.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* <div>
                <label className="block text-sm font-semibold text-primary mb-2">
                  Budget Range
                </label>
                <select
                  value={formData.budget}
                  onChange={(e) => handleInputChange('budget', e.target.value)}
                  className="w-full px-4 py-1 sm:py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-accent cinematic-transition"
                >
                  <option value="">Select budget range</option>
                  {BUDGET_OPTIONS.map((range) => (
                    <option key={range.id} value={range.id}>
                      {range.range}
                    </option>
                  ))}
                </select>
              </div> */}
            </div>
          </div>
        </div>

        <div className="bg-surface rounded-xl p-2 sm:p-6">
          <h3 className="text-xl font-bold text-primary mb-2 sm:mb-6 flex items-center space-x-2">
            <Icon name="Plus" size={20} strokeWidth={2} />
            <span>Additional Information</span>
          </h3>
          
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">
              Special Requirements or Considerations
            </label>
            <textarea
              value={formData.specificRequirements}
              onChange={(e) => handleInputChange('specificRequirements', e.target.value)}
              rows={3}
              className="w-full px-4 py-1 sm:py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-accent cinematic-transition resize-none"
              placeholder="Any special requirements or important details..."
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className="flex items-center justify-center lg:space-x-2 px-6 py-3 text-xs font-semibold uppercase text-white transition-command sm:text-sm bg-red-700 rounded-lg shadow-emergency hover:bg-accent/90"
          >
            <span>Continue to Scheduling</span>
            <Icon name="ArrowRight" size={20} strokeWidth={2} />
          </button>
        </div>
      </form>
    </div>
  );
};
export default IntakeForm;